import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

// components
import UnsavedChangesWarningDialog from '@/ui/components/Dialog/UnsavedChangesWarningDialog'
import FeedbackInputArea from '@/ui/components/FeedBackInputArea/FeedBackInputArea'
import { ActivityIndicator, Button } from '@bosch/react-frok'

// utils
import { AssistantAction, getAssistantIllustration } from '@/hooks/useAssistantIllustration'
import api from '@/utils/api/api'
import { useSpace } from '@/hooks/useSpace'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useConfig } from '@/hooks/useConfig'

// styles
import './Feedback.scss'

const Feedback: React.FC = () => {
    const [waitingForResponse, setWaitingForResponse] = useState<boolean>(false)
    const [feedbackMessage, setFeedbackMessage] = useState<string>('')
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)

    const { getActiveAssistant } = useSpace()
    const { information, getApiToken } = useAuthentication()
    const { t } = useTranslation()
    const { getConfig } = useConfig()

    const config = getConfig()
    const assistant = getActiveAssistant()
    const userId: string | undefined = information.userId
    const holdsText = feedbackMessage.length > 0

    const sendGeneralFeedback = async (
        userId: string,
        feedback: string
    ): Promise<void> => {
        try {
            const token = await getApiToken()
            const url = `${config?.endpoint}/generalFeedback`
            const query = {
                userID: userId,
            }
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }
            const body = JSON.stringify({ feedback: feedback })

            await api.post(url, query, body, headers)
        } catch (error) {
            console.error('Error sending general feedback: ', error)
        }
    }

    const sendFeedback = async (): Promise<void> => {
        if (!userId) return

        setWaitingForResponse(true)
        await sendGeneralFeedback(userId, feedbackMessage)
        setShowSuccessMessage(true)
        setWaitingForResponse(false)
        setFeedbackMessage('')
    }

    const closeSuccessMessage = (): void => {
        setShowSuccessMessage(false)
    }

    const handleUpdateText = (text: string): void => {
        setFeedbackMessage(text)
    }
    return (
        <div className="feedback-page">
            <div className="feedback-wrapper">
                <div className="feedback-content">
                    <UnsavedChangesWarningDialog
                        when={holdsText}
                        title={t('generalFeedback:warningDialogTitle')}
                        message={t('generalFeedback:warningDialogDesc')}
                        confirmLabel={t(
                            'generalFeedback:warningDialogConfirmLabel'
                        )}
                        cancelLabel={t('generalFeedback:warningDialogCancelLabel')}
                    />
                    {!showSuccessMessage ? (
                        <>
                            <span className="feedback-title">
                                {t('generalFeedback:title')}
                            </span>
                            <FeedbackInputArea
                                text={feedbackMessage}
                                updateText={handleUpdateText}
                                aiInstance={assistant?.aiAdapter}
                                waitingForResponse={waitingForResponse}
                                disabled={waitingForResponse}
                                isLoading={waitingForResponse}
                                assistant={assistant}
                            />
                            <div className="feedback-actions">
                                <span className="area-description">
                                    {t('generalFeedback:description')}
                                </span>
                                <div className="action-button-area">
                                    {!waitingForResponse ? (
                                        <Button
                                            mode="primary"
                                            onClick={sendFeedback}
                                            disabled={!holdsText}
                                            label={t(
                                                'generalFeedback:sendFeedback'
                                            )}
                                        />
                                    ) : (
                                        <ActivityIndicator
                                            size="small"
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="feedback-result">
                            <img
                                className="result-icon"
                                src={getAssistantIllustration(AssistantAction.WAVING)}
                                alt="result"
                            />
                            <span className="success-message">
                                {t('generalFeedback:successMessage')}
                            </span>
                            <Button
                                mode="secondary"
                                onClick={closeSuccessMessage}
                                label={t('generalFeedback:newFeeback')}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Feedback
